// File: src/pages/CancelOrder.jsx
// Sales Flow page
// - Uses view_create_order_history_database with database-financialYearSelected
// - Merges b2bData, b2csData and b2clData
// - Per-tab search, Excel export and column selector
// - OTP Log and Financial Year selectors removed

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Table,
  Badge,
  Nav,
  NavItem,
  NavLink,
  Label,
  Input,
} from "reactstrap";
import swal from "sweetalert";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { ImDownload } from "react-icons/im";
import {
  FaChevronDown,
  FaChevronRight,
  FaColumns,
  FaSearch,
} from "react-icons/fa";

import { _Get, _Delete } from "../import/ApiEndPoint/ApiCalling";
import {
  view_create_order_history_database,
  Delete_Sales,
  Delete_Sales_order,
  Last_Ledger_Balance,
} from "../import/ApiEndPoint/Api";

import { HsnSummaryCalculation } from "../import/HsnSummaryCalculation";
import InvoiceGenerator from "./InvoiceGenerator";
import LinkCell from "../components/LinkCell";

/* ------------------------------------------------------------------ */
/* Helpers                                                            */
/* ------------------------------------------------------------------ */

const S = (v) => (v === null || v === undefined ? "" : String(v));
const N = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

const firstNum = (...vals) => {
  for (const v of vals) {
    if (v !== null && v !== undefined && v !== "" && !Number.isNaN(Number(v))) {
      return Number(v);
    }
  }
  return null;
};

const fmtDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return S(value);
  return date.toLocaleString("en-IN", { hour12: true });
};

const fmtMoney = (value) => N(value).toFixed(2);

const statusOf = (order) =>
  S(order?.status ?? order?.orderStatus ?? order?.state ?? order?.currentStatus)
    .trim()
    .toLowerCase();

const getInvoiceNo = (row) =>
  row?.invoiceId ??
  row?.InvoiceId ??
  row?.invoiceNo ??
  row?.Invoice_No ??
  row?.SalesInvoiceNo ??
  "—";

const getRoundOff = (row) =>
  N(
    firstNum(
      row?.roundOff,
      row?.roundoff,
      row?.RoundOff,
      row?.round_off,
      row?.Round_Off,
      0,
    ),
  );

const getRowDate = (row) =>
  new Date(row?.date || row?.createdAt || row?.updatedAt || Date.now());

const getFinancialYearLabel = (dateValue = new Date()) => {
  const date = new Date(dateValue);
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const startYear = month >= 4 ? year : year - 1;
  return `${startYear}-${String(startYear + 1).slice(-2)}`;
};

const getFinancialYearStartYear = (financialYear) => {
  const startYear = Number(String(financialYear || "").split("-")[0]);
  return Number.isFinite(startYear) ? startYear : new Date().getFullYear();
};

const getFYMonthMeta = (dateValue) => {
  const date = new Date(dateValue);
  const month = date.getMonth() + 1;
  const year = date.getFullYear();
  const financialYear = getFinancialYearLabel(date);

  const labelMap = {
    4: "April",
    5: "May",
    6: "June",
    7: "July",
    8: "August",
    9: "September",
    10: "October",
    11: "November",
    12: "December",
    1: "January",
    2: "February",
    3: "March",
  };

  const sortIndexMap = {
    4: 1,
    5: 2,
    6: 3,
    7: 4,
    8: 5,
    9: 6,
    10: 7,
    11: 8,
    12: 9,
    1: 10,
    2: 11,
    3: 12,
  };

  const financialYearStart = getFinancialYearStartYear(financialYear);
  const displayYear = month >= 4 ? financialYearStart : financialYearStart + 1;

  return {
    financialYear,
    month,
    label: `${labelMap[month]} ${displayYear}`,
    sortIndex: sortIndexMap[month],
    displayYear,
    realYear: year,
  };
};

const MONTHS_FY = [
  { key: 4, label: "April" },
  { key: 5, label: "May" },
  { key: 6, label: "June" },
  { key: 7, label: "July" },
  { key: 8, label: "August" },
  { key: 9, label: "September" },
  { key: 10, label: "October" },
  { key: 11, label: "November" },
  { key: 12, label: "December" },
  { key: 1, label: "January" },
  { key: 2, label: "February" },
  { key: 3, label: "March" },
];

const QUARTERS = [
  { value: "Q1", label: "Q1 (Apr - Jun)", months: [4, 5, 6] },
  { value: "Q2", label: "Q2 (Jul - Sep)", months: [7, 8, 9] },
  { value: "Q3", label: "Q3 (Oct - Dec)", months: [10, 11, 12] },
  { value: "Q4", label: "Q4 (Jan - Mar)", months: [1, 2, 3] },
];

const HALVES = [
  { value: "H1", label: "H1 (Apr - Sep)", months: [4, 5, 6, 7, 8, 9] },
  { value: "H2", label: "H2 (Oct - Mar)", months: [10, 11, 12, 1, 2, 3] },
];

const TABS = [
  "ORDERS",
  "FORWARDED_TO_WAREHOUSE",
  "ORDER_INVOICE",
  "READY_TO_DISPATCH",
  "OUT_FOR_DELIVERY",
  "COMPLETED",
  "CANCELLED",
];

const TAB_LABELS = {
  ORDERS: "Orders",
  FORWARDED_TO_WAREHOUSE: "Forwarded To Warehouse",
  ORDER_INVOICE: "Order Invoice",
  READY_TO_DISPATCH: "Ready To Dispatch",
  OUT_FOR_DELIVERY: "Out For Delivery",
  COMPLETED: "Completed",
  CANCELLED: "Cancelled",
};

const COLUMN_META = {
  month: { label: "Month / Row" },
  status: { label: "Status" },
  number: { label: "Order / Invoice No" },
  dateTime: { label: "Date / Time" },
  partyName: { label: "Party Name" },
  salesPerson: { label: "Sales Person" },
  salesPersonMobile: { label: "Sales Person Mobile" },
  basicTotal: { label: "Basic Total" },
  cgst: { label: "CGST" },
  sgst: { label: "SGST" },
  igst: { label: "IGST" },
  roundOff: { label: "Round Off" },
  grandTotal: { label: "Grand Total" },
  packages: { label: "No. of Packages" },
  deliveryBoy: { label: "Delivery Boy" },
  actions: { label: "Actions" },
};

const COMMON_COLUMNS = [
  "month",
  "status",
  "number",
  "dateTime",
  "partyName",
  "basicTotal",
  "cgst",
  "sgst",
  "igst",
  "roundOff",
  "grandTotal",
];

const TAB_COLUMNS = {
  ORDERS: [
    "month",
    "status",
    "number",
    "dateTime",
    "partyName",
    "salesPerson",
    "salesPersonMobile",
    "basicTotal",
    "cgst",
    "sgst",
    "igst",
    "roundOff",
    "grandTotal",
    "packages",
    "deliveryBoy",
    "actions",
  ],
  FORWARDED_TO_WAREHOUSE: [...COMMON_COLUMNS],
  ORDER_INVOICE: [...COMMON_COLUMNS],
  READY_TO_DISPATCH: [...COMMON_COLUMNS, "actions"],
  OUT_FOR_DELIVERY: [...COMMON_COLUMNS],
  COMPLETED: [...COMMON_COLUMNS, "actions"],
  CANCELLED: [...COMMON_COLUMNS],
};

const makeInitialTabState = (valueFactory) =>
  TABS.reduce((acc, tab) => {
    acc[tab] = valueFactory(tab);
    return acc;
  }, {});

/* ------------------------------------------------------------------ */
/* Preserve API totals                                                */
/* ------------------------------------------------------------------ */

function patchTotals(order) {
  const patched = JSON.parse(JSON.stringify(order || {}));

  let calculatedBasic = 0;
  let calculatedCgst = 0;
  let calculatedSgst = 0;
  let calculatedIgst = 0;

  (patched.orderItems || []).forEach((item) => {
    const qty = N(item?.qty);
    const price = N(
      firstNum(
        item?.BasicPrice,
        item?.price,
        item?.productId?.SalesRate,
        item?.productId?.Product_MRP,
        0,
      ),
    );

    const lineBasic = +(
      firstNum(item?.BasicTotal, item?.taxableAmount, price * qty, 0) || 0
    ).toFixed(2);

    const gst = N(
      firstNum(item?.gstPercentage, item?.gst, item?.productId?.GSTRate, 0),
    );

    let itemCgst = firstNum(item?.cgstTotal, item?.cgstAmount, item?.CGSTTotal);
    let itemSgst = firstNum(item?.sgstTotal, item?.sgstAmount, item?.SGSTTotal);
    let itemIgst = firstNum(item?.igstTotal, item?.igstAmount, item?.IGSTTotal);

    if (itemCgst === null && itemSgst === null && itemIgst === null) {
      const isIgst =
        patched?.igstTaxType === 1 ||
        patched?.igstTaxType === true ||
        String(patched?.igstTaxType).toLowerCase() === "true";

      if (isIgst) {
        itemIgst = +((lineBasic * gst) / 100).toFixed(2);
        itemCgst = 0;
        itemSgst = 0;
      } else {
        itemCgst = +((lineBasic * (gst / 2)) / 100).toFixed(2);
        itemSgst = +((lineBasic * (gst / 2)) / 100).toFixed(2);
        itemIgst = 0;
      }
    }

    item.BasicPrice = +price.toFixed(2);
    item.BasicTotal = lineBasic;
    item.cgstTotal = N(itemCgst);
    item.sgstTotal = N(itemSgst);
    item.igstTotal = N(itemIgst);
    item.grandTotal = +(
      firstNum(
        item?.grandTotal,
        lineBasic + N(itemCgst) + N(itemSgst) + N(itemIgst),
        0,
      ) || 0
    ).toFixed(2);

    calculatedBasic += lineBasic;
    calculatedCgst += N(itemCgst);
    calculatedSgst += N(itemSgst);
    calculatedIgst += N(itemIgst);
  });

  const apiAmount = firstNum(
    patched?.amount,
    patched?.Amount,
    patched?.basicTotal,
    patched?.BasicTotal,
    patched?.taxableAmount,
  );
  const apiCgst = firstNum(
    patched?.cgstTotal,
    patched?.CGSTTotal,
    patched?.cgst,
    patched?.CGST,
  );
  const apiSgst = firstNum(
    patched?.sgstTotal,
    patched?.SGSTTotal,
    patched?.sgst,
    patched?.SGST,
  );
  const apiIgst = firstNum(
    patched?.igstTotal,
    patched?.IGSTTotal,
    patched?.igst,
    patched?.IGST,
  );
  const apiRoundOff = firstNum(
    patched?.roundOff,
    patched?.roundoff,
    patched?.RoundOff,
    patched?.round_off,
    patched?.Round_Off,
    0,
  );
  const apiGrandTotal = firstNum(
    patched?.grandTotal,
    patched?.GrandTotal,
    patched?.grand_total,
    patched?.totalAmount,
    patched?.TotalAmount,
  );

  patched.amount = +(apiAmount !== null ? apiAmount : calculatedBasic).toFixed(
    2,
  );
  patched.cgstTotal = +(apiCgst !== null ? apiCgst : calculatedCgst).toFixed(2);
  patched.sgstTotal = +(apiSgst !== null ? apiSgst : calculatedSgst).toFixed(2);
  patched.igstTotal = +(apiIgst !== null ? apiIgst : calculatedIgst).toFixed(2);
  patched.roundOff = +N(apiRoundOff).toFixed(2);
  patched.gstTotal = +(
    patched.cgstTotal +
    patched.sgstTotal +
    patched.igstTotal
  ).toFixed(2);
  patched.grandTotal = +(
    apiGrandTotal !== null
      ? apiGrandTotal
      : patched.amount +
        patched.cgstTotal +
        patched.sgstTotal +
        patched.igstTotal +
        patched.roundOff
  ).toFixed(2);

  return patched;
}

/* ------------------------------------------------------------------ */
/* Totals and grouping                                                */
/* ------------------------------------------------------------------ */

const makeTotals = (rows = []) => ({
  basicTotal: rows.reduce((sum, row) => sum + N(row?.amount), 0),
  cgstTotal: rows.reduce((sum, row) => sum + N(row?.cgstTotal), 0),
  sgstTotal: rows.reduce((sum, row) => sum + N(row?.sgstTotal), 0),
  igstTotal: rows.reduce((sum, row) => sum + N(row?.igstTotal), 0),
  roundOff: rows.reduce((sum, row) => sum + getRoundOff(row), 0),
  grandTotal: rows.reduce((sum, row) => sum + N(row?.grandTotal), 0),
  packages: rows.reduce(
    (sum, row) => sum + N(row?.NoOfPackage ?? row?.NoOfPackages),
    0,
  ),
});

function groupRowsByMonth(rows = []) {
  const bucket = {};

  rows.forEach((row) => {
    const meta = getFYMonthMeta(getRowDate(row));
    const key = `${meta.financialYear}-${meta.month}`;

    if (!bucket[key]) {
      bucket[key] = {
        key,
        label: meta.label,
        month: meta.month,
        financialYear: meta.financialYear,
        sortIndex: meta.sortIndex,
        rows: [],
      };
    }

    bucket[key].rows.push(row);
  });

  return Object.values(bucket)
    .map((group) => ({
      ...group,
      rows: group.rows.sort((a, b) => getRowDate(b) - getRowDate(a)),
      totals: makeTotals(group.rows),
    }))
    .sort((a, b) => a.sortIndex - b.sortIndex);
}

const rowSearchText = (row) => {
  const products = (Array.isArray(row?.orderItems) ? row.orderItems : [])
    .map(
      (item) =>
        item?.productId?.Product_Title ||
        item?.productData?.Product_Title ||
        item?.Product_Title ||
        "",
    )
    .join(" ");

  return [
    row?.status,
    row?.orderNo,
    getInvoiceNo(row),
    row?.date,
    row?.createdAt,
    row?.partyId?.CompanyName,
    row?.partyId?.fullName,
    row?.salesPersonName,
    row?.userId?.name,
    row?.salesPersonMobile,
    row?.userId?.mobile,
    row?.amount,
    row?.cgstTotal,
    row?.sgstTotal,
    row?.igstTotal,
    getRoundOff(row),
    row?.grandTotal,
    row?.NoOfPackage,
    row?.NoOfPackages,
    row?.deliveryBoy?.name,
    row?.deliveryBoyName,
    products,
  ]
    .map(S)
    .join(" ")
    .toLowerCase();
};

/* ------------------------------------------------------------------ */
/* Excel                                                              */
/* ------------------------------------------------------------------ */

const getColumnHeader = (key, activeTab) => {
  if (key === "number") {
    return activeTab === "COMPLETED" ? "Invoice No" : "Order No";
  }
  return COLUMN_META[key]?.label || key;
};

const getExcelDetailValue = (row, key, activeTab, groupLabel, index) => {
  switch (key) {
    case "month":
      return `${groupLabel} #${index + 1}`;
    case "status":
      return row?.status || "";
    case "number":
      return activeTab === "COMPLETED" ? getInvoiceNo(row) : row?.orderNo || "";
    case "dateTime":
      return fmtDateTime(row?.date || row?.createdAt);
    case "partyName":
      return row?.partyId?.CompanyName || row?.partyId?.fullName || "";
    case "salesPerson":
      return row?.salesPersonName || row?.userId?.name || "";
    case "salesPersonMobile":
      return row?.salesPersonMobile || row?.userId?.mobile || "";
    case "basicTotal":
      return N(row?.amount);
    case "cgst":
      return N(row?.cgstTotal);
    case "sgst":
      return N(row?.sgstTotal);
    case "igst":
      return N(row?.igstTotal);
    case "roundOff":
      return getRoundOff(row);
    case "grandTotal":
      return N(row?.grandTotal);
    case "packages":
      return N(row?.NoOfPackage ?? row?.NoOfPackages);
    case "deliveryBoy":
      return row?.deliveryBoy?.name || row?.deliveryBoyName || "";
    default:
      return "";
  }
};

const getExcelTotalValue = (totals, key, label, isFinal = false) => {
  switch (key) {
    case "month":
      return isFinal ? "FINAL TOTAL" : `${label} TOTAL`;
    case "status":
      return isFinal ? "Final Total" : "Month Total";
    case "basicTotal":
      return N(totals?.basicTotal);
    case "cgst":
      return N(totals?.cgstTotal);
    case "sgst":
      return N(totals?.sgstTotal);
    case "igst":
      return N(totals?.igstTotal);
    case "roundOff":
      return N(totals?.roundOff);
    case "grandTotal":
      return N(totals?.grandTotal);
    case "packages":
      return N(totals?.packages);
    default:
      return "";
  }
};

function buildExcelData(groups, activeTab, visibleColumnKeys) {
  let exportKeys = visibleColumnKeys.filter((key) => key !== "actions");
  if (!exportKeys.length) exportKeys = ["month"];

  const rows = [];

  groups.forEach((group) => {
    const monthTotalRow = {};
    exportKeys.forEach((key) => {
      monthTotalRow[getColumnHeader(key, activeTab)] = getExcelTotalValue(
        group.totals,
        key,
        group.label,
        false,
      );
    });
    rows.push(monthTotalRow);

    group.rows.forEach((row, index) => {
      const detailRow = {};
      exportKeys.forEach((key) => {
        detailRow[getColumnHeader(key, activeTab)] = getExcelDetailValue(
          row,
          key,
          activeTab,
          group.label,
          index,
        );
      });
      rows.push(detailRow);
    });
  });

  const allRows = groups.flatMap((group) => group.rows);
  const finalTotals = makeTotals(allRows);
  const finalRow = {};

  exportKeys.forEach((key) => {
    finalRow[getColumnHeader(key, activeTab)] = getExcelTotalValue(
      finalTotals,
      key,
      "",
      true,
    );
  });
  rows.push(finalRow);

  return rows;
}

function exportToExcel(
  groups,
  activeTab,
  visibleColumnKeys,
  financialYearSelected,
) {
  const data = buildExcelData(groups, activeTab, visibleColumnKeys);
  const worksheet = XLSX.utils.json_to_sheet(data);
  const workbook = XLSX.utils.book_new();

  const sheetName = TAB_LABELS[activeTab].slice(0, 31);
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

  const buffer = XLSX.write(workbook, {
    bookType: "xlsx",
    type: "array",
  });

  saveAs(
    new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }),
    `SalesFlow_${activeTab}_${financialYearSelected}.xlsx`,
  );
}

/* ------------------------------------------------------------------ */
/* Styling                                                            */
/* ------------------------------------------------------------------ */

const injectOnce = () => {
  if (document.getElementById("sales-flow-updated-css")) return;

  const style = document.createElement("style");
  style.id = "sales-flow-updated-css";
  style.innerHTML = `
    .sf-page{width:100%;}
    .sf-header-card,.sf-filter-card,.sf-table-card{
      border:1px solid #e5e7eb;
      border-radius:12px;
      box-shadow:0 2px 8px rgba(15,23,42,.04);
    }
    .sf-title-row{display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap;}
    .sf-title{font-size:20px;font-weight:800;color:#0f172a;margin:0;}
    .sf-subtitle{font-size:12px;color:#64748b;margin-top:3px;}
    .sf-count{display:inline-flex;align-items:center;justify-content:center;min-width:30px;height:26px;padding:0 9px;border-radius:999px;background:#eef2ff;color:#3730a3;font-weight:800;margin-left:8px;}
    .sf-fy-badge{background:#f8fafc;border:1px solid #e2e8f0;color:#334155;padding:6px 10px;border-radius:9px;font-size:12px;font-weight:700;}
    .sf-filterbar{display:flex;align-items:end;gap:10px;flex-wrap:wrap;}
    .sf-filteritem{min-width:180px;}
    .sf-filteritem label{font-size:12px;font-weight:700;margin-bottom:4px;color:#334155;}
    .sf-tabs{border-bottom:1px solid #e5e7eb;gap:4px;flex-wrap:nowrap;overflow-x:auto;overflow-y:hidden;padding-bottom:1px;}
    .sf-tabs .nav-link{white-space:nowrap;border:0;border-radius:9px 9px 0 0;color:#475569;font-size:13px;font-weight:700;padding:9px 12px;cursor:pointer;}
    .sf-tabs .nav-link.active{background:#eef2ff;color:#3730a3;border-bottom:2px solid #4f46e5;}
    .sf-tab-count{margin-left:6px;background:#fff;border:1px solid #e2e8f0;color:#475569;}
    .sf-toolbar{display:flex;align-items:center;justify-content:space-between;gap:10px;flex-wrap:wrap;margin-bottom:12px;padding:10px;border:1px solid #e5e7eb;border-radius:10px;background:#f8fafc;}
    .sf-toolbar-left,.sf-toolbar-right{display:flex;align-items:center;gap:8px;flex-wrap:wrap;}
    .sf-search-wrap{position:relative;min-width:280px;}
    .sf-search-icon{position:absolute;left:10px;top:50%;transform:translateY(-50%);color:#94a3b8;z-index:2;}
    .sf-search-input{padding-left:32px!important;height:34px;}
    .sf-result-text{font-size:12px;color:#64748b;font-weight:600;}
    .sf-column-wrap{position:relative;}
    .sf-column-menu{position:absolute;right:0;top:40px;z-index:30;width:250px;max-height:360px;overflow:auto;background:#fff;border:1px solid #dbe3ee;border-radius:10px;box-shadow:0 14px 32px rgba(15,23,42,.16);padding:10px;}
    .sf-column-menu-head{display:flex;align-items:center;justify-content:space-between;gap:8px;padding-bottom:8px;margin-bottom:6px;border-bottom:1px solid #eef2f7;}
    .sf-column-menu-title{font-size:13px;font-weight:800;color:#0f172a;}
    .sf-column-option{display:flex;align-items:center;gap:8px;padding:6px 4px;font-size:13px;color:#334155;cursor:pointer;}
    .sf-column-option:hover{background:#f8fafc;border-radius:6px;}
    .sf-table-wrap{width:100%;overflow:auto;border:1px solid #e5e7eb;border-radius:10px;}
    .sf-table{margin:0;min-width:1050px;font-size:12px;}
    .sf-table thead th{background:#f8fafc;color:#334155;font-weight:800;white-space:nowrap;vertical-align:middle;position:sticky;top:0;z-index:2;}
    .sf-table tbody td{white-space:nowrap;vertical-align:middle;}
    .sf-month-row{background:#eef6ff;font-weight:800;cursor:pointer;}
    .sf-month-row:hover{background:#e3efff;}
    .sf-order-row{cursor:pointer;}
    .sf-order-row:hover td{background:#f8fbff;}
    .sf-month-label{display:flex;align-items:center;gap:8px;}
    .sf-final-row td{font-weight:800;background:#f8fafc;}
    .sf-muted{color:#64748b;}
    .sf-action-buttons{display:flex;gap:5px;flex-wrap:wrap;}
    .sf-empty{padding:24px;text-align:center;color:#64748b;}
    @media(max-width:768px){
      .sf-search-wrap{min-width:100%;width:100%;}
      .sf-toolbar-left,.sf-toolbar-right{width:100%;}
      .sf-toolbar-right{justify-content:flex-end;}
      .sf-filteritem{min-width:100%;}
    }
  `;

  document.head.appendChild(style);
};

/* ------------------------------------------------------------------ */
/* Dynamic grouped table                                              */
/* ------------------------------------------------------------------ */

function MonthGroupedInvoiceTable({
  groups,
  activeTab,
  visibleColumnKeys,
  monthExpanded,
  toggleMonth,
  onDelete,
  onView,
  onInvoiceNavigate,
  onOpenInvoice,
  onEditOrder,
}) {
  const grandTotals = makeTotals(groups.flatMap((group) => group.rows));
  const visibleSet = new Set(visibleColumnKeys);
  const completedMode = activeTab === "COMPLETED";
  const orderNoAsLink = activeTab === "ORDER_INVOICE";

  const renderHeader = (key) => (
    <th key={key}>{getColumnHeader(key, activeTab)}</th>
  );

  const renderMonthCell = (group, key) => {
    switch (key) {
      case "month":
        return (
          <td key={key}>
            <div className="sf-month-label">
              {monthExpanded[group.key] ? (
                <FaChevronDown />
              ) : (
                <FaChevronRight />
              )}
              <span>{group.label}</span>
            </div>
          </td>
        );
      case "status":
        return <td key={key}>Month Total</td>;
      case "basicTotal":
        return <td key={key}>{fmtMoney(group.totals.basicTotal)}</td>;
      case "cgst":
        return <td key={key}>{fmtMoney(group.totals.cgstTotal)}</td>;
      case "sgst":
        return <td key={key}>{fmtMoney(group.totals.sgstTotal)}</td>;
      case "igst":
        return <td key={key}>{fmtMoney(group.totals.igstTotal)}</td>;
      case "roundOff":
        return <td key={key}>{fmtMoney(group.totals.roundOff)}</td>;
      case "grandTotal":
        return <td key={key}>{fmtMoney(group.totals.grandTotal)}</td>;
      case "packages":
        return <td key={key}>{group.totals.packages}</td>;
      default:
        return <td key={key}>—</td>;
    }
  };

  const renderDetailCell = (row, rowIndex, key) => {
    switch (key) {
      case "month":
        return (
          <td key={key}>
            <span className="sf-muted">#{rowIndex + 1}</span>
          </td>
        );
      case "status":
        return (
          <td key={key}>
            <Badge
              color={
                /completed/i.test(row?.status || "")
                  ? "success"
                  : /cancel/i.test(row?.status || "")
                    ? "danger"
                    : /pending/i.test(row?.status || "")
                      ? "warning"
                      : "secondary"
              }
            >
              {row?.status || "—"}
            </Badge>
          </td>
        );
      case "number":
        return (
          <td key={key}>
            {orderNoAsLink ? (
              <LinkCell
                onClick={(event) => {
                  event?.stopPropagation?.();
                  onInvoiceNavigate?.(row);
                }}
              >
                {row?.orderNo || "—"}
              </LinkCell>
            ) : completedMode ? (
              getInvoiceNo(row)
            ) : (
              row?.orderNo || "—"
            )}
          </td>
        );
      case "dateTime":
        return <td key={key}>{fmtDateTime(row?.date || row?.createdAt)}</td>;
      case "partyName":
        return (
          <td key={key}>
            {row?.partyId?.CompanyName || row?.partyId?.fullName || "—"}
          </td>
        );
      case "salesPerson":
        return (
          <td key={key}>{row?.salesPersonName || row?.userId?.name || "—"}</td>
        );
      case "salesPersonMobile":
        return (
          <td key={key}>
            {row?.salesPersonMobile || row?.userId?.mobile || "—"}
          </td>
        );
      case "basicTotal":
        return <td key={key}>{fmtMoney(row?.amount)}</td>;
      case "cgst":
        return <td key={key}>{fmtMoney(row?.cgstTotal)}</td>;
      case "sgst":
        return <td key={key}>{fmtMoney(row?.sgstTotal)}</td>;
      case "igst":
        return <td key={key}>{fmtMoney(row?.igstTotal)}</td>;
      case "roundOff":
        return <td key={key}>{fmtMoney(getRoundOff(row))}</td>;
      case "grandTotal":
        return <td key={key}>{fmtMoney(row?.grandTotal)}</td>;
      case "packages":
        return (
          <td key={key}>{row?.NoOfPackage ?? row?.NoOfPackages ?? "—"}</td>
        );
      case "deliveryBoy":
        return (
          <td key={key}>
            {row?.deliveryBoy?.name || row?.deliveryBoyName || "—"}
          </td>
        );
      case "actions":
        return (
          <td key={key}>
            <div className="sf-action-buttons">
              {activeTab === "ORDERS" && /pending/i.test(row?.status || "") && (
                <Button
                  size="sm"
                  color="danger"
                  onClick={(event) => {
                    event.stopPropagation();
                    onDelete?.(row);
                  }}
                >
                  Delete
                </Button>
              )}

              {["ORDERS", "READY_TO_DISPATCH", "COMPLETED"].includes(
                activeTab,
              ) && (
                <Button
                  size="sm"
                  color="info"
                  onClick={(event) => {
                    event.stopPropagation();
                    onView?.(row);
                  }}
                >
                  View
                </Button>
              )}

              {activeTab === "READY_TO_DISPATCH" &&
                /delivery boy assigned/i.test(row?.status || "") && (
                  <Button
                    size="sm"
                    color="success"
                    onClick={(event) => {
                      event.stopPropagation();
                      onInvoiceNavigate?.(row);
                    }}
                  >
                    Invoice
                  </Button>
                )}

              {activeTab === "COMPLETED" && (
                <Button
                  size="sm"
                  color="primary"
                  onClick={(event) => {
                    event.stopPropagation();
                    onOpenInvoice?.(row);
                  }}
                >
                  Invoice
                </Button>
              )}
            </div>
          </td>
        );
      default:
        return <td key={key}>—</td>;
    }
  };

  const renderFinalCell = (key) => {
    switch (key) {
      case "month":
        return <td key={key}>Final Total</td>;
      case "basicTotal":
        return <td key={key}>{fmtMoney(grandTotals.basicTotal)}</td>;
      case "cgst":
        return <td key={key}>{fmtMoney(grandTotals.cgstTotal)}</td>;
      case "sgst":
        return <td key={key}>{fmtMoney(grandTotals.sgstTotal)}</td>;
      case "igst":
        return <td key={key}>{fmtMoney(grandTotals.igstTotal)}</td>;
      case "roundOff":
        return <td key={key}>{fmtMoney(grandTotals.roundOff)}</td>;
      case "grandTotal":
        return <td key={key}>{fmtMoney(grandTotals.grandTotal)}</td>;
      case "packages":
        return <td key={key}>{grandTotals.packages}</td>;
      default:
        return <td key={key}></td>;
    }
  };

  if (!visibleColumnKeys.length) {
    return <div className="sf-empty">Select at least one column.</div>;
  }

  return (
    <div className="sf-table-wrap">
      <Table bordered hover size="sm" className="sf-table">
        <thead>
          <tr>{visibleColumnKeys.map(renderHeader)}</tr>
        </thead>
        <tbody>
          {!groups.length ? (
            <tr>
              <td colSpan={visibleColumnKeys.length} className="sf-empty">
                No records found.
              </td>
            </tr>
          ) : (
            <>
              {groups.map((group) => (
                <React.Fragment key={group.key}>
                  <tr
                    className="sf-month-row"
                    onClick={() => toggleMonth(group.key)}
                  >
                    {visibleColumnKeys.map((key) =>
                      renderMonthCell(group, key),
                    )}
                  </tr>

                  {monthExpanded[group.key] &&
                    group.rows.map((row, index) => (
                      <tr
                        key={`${group.key}-${row?._id || index}`}
                        className="sf-order-row"
                        data-order-id={row?._id || ""}
                        onClick={() => onEditOrder?.(row)}
                        title="Click to edit this order"
                      >
                        {visibleColumnKeys.map((key) =>
                          renderDetailCell(row, index, key),
                        )}
                      </tr>
                    ))}
                </React.Fragment>
              ))}

              <tr className="sf-final-row">
                {visibleColumnKeys.map(renderFinalCell)}
              </tr>
            </>
          )}
        </tbody>
      </Table>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Page                                                               */
/* ------------------------------------------------------------------ */

export default function CancelOrder() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [activeTab, setActiveTab] = useState("ORDERS");
  const [allRows, setAllRows] = useState([]);

  const [financialYearSelected, setFinancialYearSelected] = useState("");
  const [selectedDatabase, setSelectedDatabase] = useState("");

  const [periodType, setPeriodType] = useState("YEARLY");
  const [selectedMonth, setSelectedMonth] = useState(4);
  const [selectedQuarter, setSelectedQuarter] = useState("Q1");
  const [selectedHalf, setSelectedHalf] = useState("H1");

  const [searchByTab, setSearchByTab] = useState(() =>
    makeInitialTabState(() => ""),
  );
  const [visibleColumnsByTab, setVisibleColumnsByTab] = useState(() =>
    makeInitialTabState((tab) => [...TAB_COLUMNS[tab]]),
  );
  const [columnMenuOpen, setColumnMenuOpen] = useState(false);

  const [monthExpanded, setMonthExpanded] = useState({});

  const [viewOpen, setViewOpen] = useState(false);
  const [viewRow, setViewRow] = useState(null);

  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [invoiceRow, setInvoiceRow] = useState(null);
  const [companyDetails, setCompanyDetails] = useState(null);
  const [billNumber, setBillNumber] = useState(null);
  const [eWayBill, setEWayBill] = useState(false);
  const [wordsNumber, setWordsNumber] = useState("");

  useEffect(() => injectOnce(), []);

  useEffect(() => {
    const loadOrders = async () => {
      setLoading(true);
      setLoadError("");

      try {
        const userInfo = JSON.parse(localStorage.getItem("userData") || "{}");
        const baseDatabase = userInfo?.database || userInfo?.db || "";
        const selectedFY =
          localStorage.getItem("financialYearSelected") ||
          getFinancialYearLabel(new Date());
        const databaseWithFinancialYear = `${baseDatabase}-${selectedFY}`;

        setFinancialYearSelected(selectedFY);
        setSelectedDatabase(databaseWithFinancialYear);

        if (!baseDatabase) {
          throw new Error("Database was not found in userData.");
        }

        // Backend route receives the selected financial-year database as params.
        const response = await _Get(
          view_create_order_history_database,
          databaseWithFinancialYear,
        );

        const mergedBuckets = [
          ...(Array.isArray(response?.b2bData) ? response.b2bData : []),
          ...(Array.isArray(response?.b2csData) ? response.b2csData : []),
          ...(Array.isArray(response?.b2clData) ? response.b2clData : []),
        ];

        // The GST buckets should be exclusive, but de-duplicate defensively.
        const uniqueOrders = [];
        const seen = new Set();

        mergedBuckets.forEach((order, index) => {
          const key = S(
            order?._id ||
              `${order?.invoiceId || order?.orderNo || "order"}-${
                order?.createdAt || order?.date || index
              }`,
          );

          if (seen.has(key)) return;
          seen.add(key);
          uniqueOrders.push(order);
        });

        const normalized = uniqueOrders
          .map((order) => {
            const patched = patchTotals(order);
            patched.orderNo =
              order?.orderNo ||
              order?.orderNumber ||
              order?.order_number ||
              order?.code ||
              order?.orderid ||
              order?.id ||
              "";
            return patched;
          })
          .sort((a, b) => getRowDate(b) - getRowDate(a));

        setAllRows(normalized);

        const storedCompanyDetails = JSON.parse(
          localStorage.getItem("Companydetail") || "null",
        );
        const finalCompanyDetails =
          storedCompanyDetails || userInfo?.CompanyDetails || null;
        setCompanyDetails(finalCompanyDetails);

        if (finalCompanyDetails?.BillNumber) {
          setBillNumber(finalCompanyDetails?.billNo || 2);
        }
      } catch (error) {
        setAllRows([]);
        setLoadError(
          error?.response?.data?.message ||
            error?.message ||
            "Unable to load sales orders.",
        );
      } finally {
        setLoading(false);
      }
    };

    loadOrders();
  }, []);

  const periodFilteredRows = useMemo(() => {
    let rows = [...allRows];

    if (periodType === "MONTHLY") {
      rows = rows.filter(
        (row) =>
          getFYMonthMeta(getRowDate(row)).month === Number(selectedMonth),
      );
    } else if (periodType === "QUARTERLY") {
      const quarter = QUARTERS.find((item) => item.value === selectedQuarter);
      const months = quarter?.months || [];
      rows = rows.filter((row) =>
        months.includes(getFYMonthMeta(getRowDate(row)).month),
      );
    } else if (periodType === "HALF_YEARLY") {
      const half = HALVES.find((item) => item.value === selectedHalf);
      const months = half?.months || [];
      rows = rows.filter((row) =>
        months.includes(getFYMonthMeta(getRowDate(row)).month),
      );
    }

    return rows;
  }, [allRows, periodType, selectedMonth, selectedQuarter, selectedHalf]);

  const categorizedRows = useMemo(() => {
    const normalize = (value) => S(value).trim().toLowerCase();
    const equals = (left, right) => normalize(left) === normalize(right);

    const categorized = {
      ORDERS: [],
      FORWARDED_TO_WAREHOUSE: [],
      ORDER_INVOICE: [],
      READY_TO_DISPATCH: [],
      OUT_FOR_DELIVERY: [],
      COMPLETED: [],
      CANCELLED: [],
    };

    periodFilteredRows.forEach((order) => {
      const status = normalize(order?.status);

      if (status.includes("pending")) categorized.ORDERS.push(order);
      if (/billing|forward/.test(status)) {
        categorized.FORWARDED_TO_WAREHOUSE.push(order);
      }

      const inDispatchPhase =
        /dispatch/.test(status) || /ready\s*to\s*dispatch/.test(status);
      const deliveryBoyAssigned = equals(status, "delivery boy assigned");
      const outForDelivery = equals(status, "out for delivery");
      const completed = /completed/.test(status);

      if (
        inDispatchPhase &&
        !deliveryBoyAssigned &&
        !outForDelivery &&
        !completed
      ) {
        categorized.ORDER_INVOICE.push(order);
      }

      if (deliveryBoyAssigned) categorized.READY_TO_DISPATCH.push(order);
      if (outForDelivery) categorized.OUT_FOR_DELIVERY.push(order);
      if (completed) categorized.COMPLETED.push(order);
      if (/cancel/.test(status)) categorized.CANCELLED.push(order);
    });

    return categorized;
  }, [periodFilteredRows]);

  const activeRows = useMemo(
    () => categorizedRows[activeTab] || [],
    [categorizedRows, activeTab],
  );

  const activeSearch = searchByTab[activeTab] || "";
  const activeVisibleColumns =
    visibleColumnsByTab[activeTab] || TAB_COLUMNS[activeTab] || [];

  const searchedActiveRows = useMemo(() => {
    const term = activeSearch.trim().toLowerCase();
    if (!term) return activeRows;
    return activeRows.filter((row) => rowSearchText(row).includes(term));
  }, [activeRows, activeSearch]);

  const activeGroups = useMemo(
    () => groupRowsByMonth(searchedActiveRows),
    [searchedActiveRows],
  );

  useEffect(() => {
    const initialExpanded = {};
    activeGroups.forEach((group) => {
      initialExpanded[group.key] = false;
    });
    setMonthExpanded(initialExpanded);
  }, [activeGroups, activeTab]);

  useEffect(() => {
    setColumnMenuOpen(false);
  }, [activeTab]);

  const toggleMonth = (key) => {
    setMonthExpanded((previous) => {
      const next = {};
      Object.keys(previous).forEach((existingKey) => {
        next[existingKey] = false;
      });
      next[key] = !previous[key];
      return next;
    });
  };

  const setActiveSearch = (value) => {
    setSearchByTab((previous) => ({
      ...previous,
      [activeTab]: value,
    }));
  };

  const toggleColumn = (key) => {
    setVisibleColumnsByTab((previous) => {
      const current = previous[activeTab] || [];
      const isVisible = current.includes(key);

      if (isVisible && current.length === 1) return previous;

      const nextKeys = isVisible
        ? current.filter((item) => item !== key)
        : TAB_COLUMNS[activeTab].filter(
            (item) => item === key || current.includes(item),
          );

      return {
        ...previous,
        [activeTab]: nextKeys,
      };
    });
  };

  const showAllColumns = () => {
    setVisibleColumnsByTab((previous) => ({
      ...previous,
      [activeTab]: [...TAB_COLUMNS[activeTab]],
    }));
  };

  const handleDelete = async (row) => {
    const pending = statusOf(row).includes("pending");
    const deleteApi = pending ? Delete_Sales_order : Delete_Sales;

    const confirmed = await swal({
      title: "Warning",
      text: "Sure you want to delete this order?",
      buttons: { cancel: "Cancel", confirm: "Delete" },
      icon: "warning",
    });

    if (confirmed !== true) return;

    try {
      await _Delete(deleteApi, row?._id);
      setAllRows((previous) =>
        previous.filter((item) => item?._id !== row?._id),
      );
      swal("Deleted", "Order removed.", "success");
    } catch (error) {
      swal("Error", error?.response?.data?.message || "Delete failed", "error");
    }
  };

  const handleView = (row) => {
    setViewRow(row || null);
    setViewOpen(true);
  };

  const handleInvoiceNavigate = (row) => {
    navigate(`/app/invoice-sales-order/${row?._id}`);
  };

  const handleEditOrder = (row) => {
    if (!row?._id) {
      swal("Order ID not found");
      return;
    }

    navigate(`/app/sales-orders/edit/${row._id}`, {
      state: {
        fromOrder: true,
        order: row,
      },
    });
  };

  const handleOpenInvoiceModal = async (row) => {
    const value = { ...(row || {}) };
    setInvoiceRow(null);
    setEWayBill(false);

    if (Number(value?.grandTotal) > 49999) {
      setEWayBill(true);
      value.ChallanStatus = true;
    }

    value.salesInvoiceStatus = true;

    try {
      value.gstDetails = HsnSummaryCalculation(value);
    } catch {
      value.gstDetails = value?.gstDetails || [];
    }

    try {
      const ledgerResponse = await _Get(
        Last_Ledger_Balance,
        value?.partyId?._id,
      );
      value.lastLedgerBalance =
        ledgerResponse?.Ledger?.debit ??
        ledgerResponse?.Ledger?.credit ??
        "Not Found";
    } catch {
      value.lastLedgerBalance = "Not Found";
    }

    const total = Number(value?.grandTotal || 0);
    setWordsNumber(
      `${new Intl.NumberFormat("en-IN", {
        maximumFractionDigits: 0,
      }).format(Math.round(total))} Rupees Only`,
    );

    setInvoiceRow(value);
    setInvoiceOpen(true);
  };

  const countForTab = (tab) => categorizedRows[tab]?.length || 0;

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center"
        style={{ marginTop: "20rem" }}
      >
        <Spinner style={{ height: "4rem", width: "4rem" }} color="primary">
          Loading...
        </Spinner>
      </div>
    );
  }

  const viewIsIgst =
    viewRow?.igstTaxType === 1 ||
    viewRow?.igstTaxType === true ||
    String(viewRow?.igstTaxType).toLowerCase() === "true";

  return (
    <div className="sf-page">
      {/* <Row className="mb-2">
        <Col>
          <Card className="sf-header-card">
            <CardBody className="py-3">
              <div className="sf-title-row">
                <div>
                  <h1 className="sf-title">
                    Sales Flow
                    <span className="sf-count">
                      {periodFilteredRows.length}
                    </span>
                  </h1>
                  <div className="sf-subtitle">
                    Orders loaded from the selected financial-year database.
                  </div>
                </div>

                <div className="sf-fy-badge">
                  FY: {financialYearSelected || "—"}
                  {selectedDatabase ? ` • ${selectedDatabase}` : ""}
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row> */}

      {loadError && (
        <Row className="mb-2">
          <Col>
            <div className="alert alert-danger mb-0">{loadError}</div>
          </Col>
        </Row>
      )}

      <Row className="mb-2">
        <Col>
          <Card className="sf-filter-card">
            <CardBody className="py-3">
              <div className="sf-filterbar">
                <div className="sf-filteritem">
                  <Label>Filter Type</Label>
                  <Input
                    type="select"
                    value={periodType}
                    onChange={(event) => setPeriodType(event.target.value)}
                  >
                    <option value="MONTHLY">Monthly</option>
                    <option value="QUARTERLY">Quarterly</option>
                    <option value="HALF_YEARLY">Half Yearly</option>
                    <option value="YEARLY">Yearly</option>
                  </Input>
                </div>

                {periodType === "MONTHLY" && (
                  <div className="sf-filteritem">
                    <Label>Month</Label>
                    <Input
                      type="select"
                      value={selectedMonth}
                      onChange={(event) =>
                        setSelectedMonth(Number(event.target.value))
                      }
                    >
                      {MONTHS_FY.map((month) => (
                        <option key={month.key} value={month.key}>
                          {month.label}
                        </option>
                      ))}
                    </Input>
                  </div>
                )}

                {periodType === "QUARTERLY" && (
                  <div className="sf-filteritem">
                    <Label>Quarter</Label>
                    <Input
                      type="select"
                      value={selectedQuarter}
                      onChange={(event) =>
                        setSelectedQuarter(event.target.value)
                      }
                    >
                      {QUARTERS.map((quarter) => (
                        <option key={quarter.value} value={quarter.value}>
                          {quarter.label}
                        </option>
                      ))}
                    </Input>
                  </div>
                )}

                {periodType === "HALF_YEARLY" && (
                  <div className="sf-filteritem">
                    <Label>Half Year</Label>
                    <Input
                      type="select"
                      value={selectedHalf}
                      onChange={(event) => setSelectedHalf(event.target.value)}
                    >
                      {HALVES.map((half) => (
                        <option key={half.value} value={half.value}>
                          {half.label}
                        </option>
                      ))}
                    </Input>
                  </div>
                )}
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Row className="mb-2">
        <Col>
          <Nav tabs className="sf-tabs">
            {TABS.map((tab) => (
              <NavItem key={tab}>
                <NavLink
                  href="#!"
                  className={activeTab === tab ? "active" : ""}
                  onClick={(event) => {
                    event.preventDefault();
                    setActiveTab(tab);
                  }}
                >
                  {TAB_LABELS[tab]}
                  <Badge color="light" className="sf-tab-count">
                    {countForTab(tab)}
                  </Badge>
                </NavLink>
              </NavItem>
            ))}
          </Nav>
        </Col>
      </Row>

      <Row>
        <Col>
          <Card className="sf-table-card">
            <CardBody>
              <div className="sf-toolbar">
                <div className="sf-toolbar-left">
                  <div className="sf-search-wrap">
                    <FaSearch className="sf-search-icon" />
                    <Input
                      className="sf-search-input"
                      placeholder={`Search ${TAB_LABELS[activeTab]}...`}
                      value={activeSearch}
                      onChange={(event) => setActiveSearch(event.target.value)}
                    />
                  </div>
                  <span className="sf-result-text">
                    Showing {searchedActiveRows.length} of {activeRows.length}
                  </span>
                </div>

                <div className="sf-toolbar-right">
                  {activeTab === "ORDERS" && (
                    <Button
                      color="primary"
                      size="sm"
                      onClick={() => navigate("/app/sales-orders/create")}
                    >
                      Create Order
                    </Button>
                  )}

                  <Button
                    color="success"
                    size="sm"
                    onClick={() =>
                      exportToExcel(
                        activeGroups,
                        activeTab,
                        activeVisibleColumns,
                        financialYearSelected,
                      )
                    }
                  >
                    <ImDownload style={{ marginRight: 6 }} />
                    Excel
                  </Button>

                  <div className="sf-column-wrap">
                    <Button
                      color="secondary"
                      outline
                      size="sm"
                      onClick={() => setColumnMenuOpen((open) => !open)}
                    >
                      <FaColumns style={{ marginRight: 6 }} />
                      Columns
                    </Button>

                    {columnMenuOpen && (
                      <div className="sf-column-menu">
                        <div className="sf-column-menu-head">
                          <span className="sf-column-menu-title">
                            Select Columns
                          </span>
                          <Button
                            color="link"
                            size="sm"
                            className="p-0"
                            onClick={showAllColumns}
                          >
                            Show All
                          </Button>
                        </div>

                        {TAB_COLUMNS[activeTab].map((key) => (
                          <label className="sf-column-option" key={key}>
                            <input
                              type="checkbox"
                              checked={activeVisibleColumns.includes(key)}
                              onChange={() => toggleColumn(key)}
                            />
                            <span>{getColumnHeader(key, activeTab)}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <MonthGroupedInvoiceTable
                groups={activeGroups}
                activeTab={activeTab}
                visibleColumnKeys={activeVisibleColumns}
                monthExpanded={monthExpanded}
                toggleMonth={toggleMonth}
                onDelete={handleDelete}
                onView={handleView}
                onInvoiceNavigate={handleInvoiceNavigate}
                onOpenInvoice={handleOpenInvoiceModal}
                onEditOrder={handleEditOrder}
              />
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Modal
        isOpen={viewOpen}
        toggle={() => setViewOpen(false)}
        style={{ maxWidth: 1000 }}
      >
        <ModalHeader toggle={() => setViewOpen(false)}>
          Order Details
        </ModalHeader>
        <ModalBody>
          {!viewRow ? null : (
            <>
              <Row className="mb-2">
                <Col>
                  <Label>Party</Label>
                  <div>
                    {viewRow?.partyId?.CompanyName ||
                      viewRow?.partyId?.fullName ||
                      "—"}
                  </div>
                </Col>
                <Col>
                  <Label>Status</Label>
                  <div>
                    <Badge color="primary">{viewRow?.status || "—"}</Badge>
                  </div>
                </Col>
                <Col>
                  <Label>Basic Total</Label>
                  <div>
                    <strong>{fmtMoney(viewRow?.amount)}</strong>
                  </div>
                </Col>
                <Col>
                  <Label>Round Off</Label>
                  <div>
                    <strong>{fmtMoney(getRoundOff(viewRow))}</strong>
                  </div>
                </Col>
                <Col>
                  <Label>Grand Total</Label>
                  <div>
                    <strong>{fmtMoney(viewRow?.grandTotal)}</strong>
                  </div>
                </Col>
              </Row>

              <Row className="mb-1">
                <Col className="d-flex justify-content-center">
                  <h5>Order Items</h5>
                </Col>
              </Row>

              <Row>
                <Col>
                  <Table responsive bordered size="sm" hover>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Product</th>
                        <th>HSN</th>
                        <th>Price</th>
                        <th>Qty</th>
                        <th>Unit</th>
                        <th>Taxable</th>
                        {viewIsIgst ? (
                          <th>IGST</th>
                        ) : (
                          <>
                            <th>SGST</th>
                            <th>CGST</th>
                          </>
                        )}
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(viewRow?.orderItems || []).map((item, index) => (
                        <tr key={item?._id || index}>
                          <td>{index + 1}</td>
                          <td>
                            {item?.productId?.Product_Title ||
                              item?.productData?.Product_Title ||
                              item?.Product_Title ||
                              "—"}
                          </td>
                          <td>
                            {item?.productId?.HSN_Code ||
                              item?.productData?.HSN_Code ||
                              item?.HSN_Code ||
                              "—"}
                          </td>
                          <td>{fmtMoney(item?.price)}</td>
                          <td>{item?.qty || 0}</td>
                          <td>{item?.primaryUnit || "—"}</td>
                          <td>
                            {fmtMoney(item?.taxableAmount ?? item?.BasicTotal)}
                          </td>
                          {viewIsIgst ? (
                            <td>
                              {fmtMoney(item?.igstRate ?? item?.igstTotal)}
                            </td>
                          ) : (
                            <>
                              <td>
                                {fmtMoney(item?.sgstRate ?? item?.sgstTotal)}
                              </td>
                              <td>
                                {fmtMoney(item?.cgstRate ?? item?.cgstTotal)}
                              </td>
                            </>
                          )}
                          <td>{fmtMoney(item?.grandTotal)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Col>
              </Row>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setViewOpen(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={invoiceOpen}
        toggle={() => setInvoiceOpen(false)}
        style={{ maxWidth: 1050 }}
      >
        <ModalHeader toggle={() => setInvoiceOpen(false)}>Invoice</ModalHeader>
        <ModalBody>
          {invoiceRow ? (
            <InvoiceGenerator
              EWayBill={eWayBill}
              CompanyDetails={companyDetails}
              BillNumber={billNumber}
              PrintData={invoiceRow}
              Applied_Charges={{}}
              AllbillMerged={[]}
              wordsNumber={wordsNumber}
              deliveryCharges=""
              otherCharges=""
              discount=""
            />
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setInvoiceOpen(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
